import { useCallback } from 'react';
import { ControllerFieldState, FieldError } from 'react-hook-form';
import { LocalizedText, LocalizedTextProps, useIsTextKey } from '@mews-framework/localization';
import { CustomValidationError } from '../types';

interface CustomFieldValidationError extends Omit<FieldError, 'message'> {
    message: CustomValidationError;
}

export type ValidationError = CustomValidationError | FieldError | LocalizedTextProps;

const hasTextKey = (error: unknown): error is LocalizedTextProps =>
    typeof error === 'object' && error != null ? 'textKey' in error : false;

const useHasTranslatableMessage = () => {
    const isTextKey = useIsTextKey();

    return useCallback(
        (error: unknown): error is CustomFieldValidationError =>
            error !== null && typeof error === 'object' && 'message' in error && isTextKey(error.message),
        [isTextKey]
    );
};

const ErrorComponent = ({ error }: { error: unknown }) => {
    const isTextKey = useIsTextKey();
    const hasTranslatableMessage = useHasTranslatableMessage();

    if (hasTranslatableMessage(error)) {
        return <LocalizedText textKey={error.message} />;
    }

    if (isTextKey(error)) {
        return <LocalizedText textKey={error} />;
    }

    if (hasTextKey(error)) {
        return <LocalizedText {...error} />;
    }

    if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
        return <>{error.message}</>;
    }

    return null;
};

/**
 * @param error - receives ValidationError as an argument but using `TextKey | object` here slows down typescript a lot
 */
export const getErrorComponent = (error?: unknown) => {
    if (!error) return null;

    return <ErrorComponent error={error} />;
};

export const getFieldStateError = (fieldState: ControllerFieldState) => fieldState.error;
